import theme from '../theme';
import statusMapping from './deficiencyStatusMapping.json';
import { DEFICIENCY_STATUSES } from './deficiencyStatusDefinitions';

const COLOR_MAP = {
    warning: theme.palette.status.warning,
    info: theme.palette.status.info,
    success: theme.palette.status.success,
    error: theme.palette.status.error,
};

export const getRoleGroup = (role) => {
    for (const [group, roles] of Object.entries(statusMapping.roleGroups)) {
        if (roles.includes(role)) return group;
    }
    return 'INTERNAL_MANAGER';
};

const normalizeToApiStatus = (status) => {
    const entry = Object.values(DEFICIENCY_STATUSES).find(
        (s) => s.value === status || s.apiValue === status
    );
    return entry ? entry.apiValue : status?.toUpperCase().replace(/ /g, '_');
};

export const getStatusDisplay = (status, role) => {
    const roleGroup = getRoleGroup(role);
    const apiStatus = normalizeToApiStatus(status);
    const mapping = statusMapping.mappings[roleGroup]?.[apiStatus];

    if (!mapping) {
        return {
            displayLabel: status || 'Unknown',
            pendingDescription: null,
            color: theme.palette.grey[600],
            visible: true,
        };
    }

    return {
        displayLabel: mapping.displayLabel,
        pendingDescription: mapping.pendingDescription,
        color: COLOR_MAP[mapping.colorToken] || theme.palette.grey[600],
        visible: mapping.visible,
    };
};

export const getStatusChipPropsForRole = (status, role) => {
    const display = getStatusDisplay(status, role);

    return {
        label: display.displayLabel,
        pendingDescription: display.pendingDescription,
        sx: {
            backgroundColor: `${display.color}15`,
            color: display.color,
            fontWeight: 500,
        },
    };
};

export const getFilterableStatuses = (role) => {
    const roleGroup = getRoleGroup(role);
    const mappings = statusMapping.mappings[roleGroup];
    if (!mappings) return [];

    const seen = new Set();
    const result = [];

    const entries = Object.entries(mappings).sort(([, a], [, b]) => a.statusOrder - b.statusOrder);

    for (const [apiStatus, mapping] of entries) {
        if (!mapping.visible) continue;
        if (seen.has(mapping.displayLabel)) continue;
        seen.add(mapping.displayLabel);
        result.push({
            displayLabel: mapping.displayLabel,
            apiStatus,
            statusOrder: mapping.statusOrder,
        });
    }

    return result;
};

const STATUS_COUNT_FIELDS = {
    SUBMITTED: 'submittedCount',
    ACCEPTED: 'acceptedCount',
    ASSIGNED: 'assignedCount',
    WORK_COMPLETED: 'pendingInternalSignoffCount',
    INTERNALLY_SIGNED_OFF: 'pendingExternalSignoffCount',
    COMPLETED: 'completedCount',
};

export const COMPACT_THRESHOLD = 6;

export const getDashboardStats = (role, countData) => {
    const roleGroup = getRoleGroup(role);
    const mappings = statusMapping.mappings[roleGroup];
    if (!mappings || !countData) return [];

    const groups = new Map();

    const entries = Object.entries(mappings).sort(([, a], [, b]) => a.statusOrder - b.statusOrder);

    for (const [apiStatus, mapping] of entries) {
        if (!mapping.visible) continue;
        if (apiStatus === 'REJECTED') continue;

        const countField = STATUS_COUNT_FIELDS[apiStatus];
        if (!countField) continue;

        const count = countData[countField] || 0;

        if (groups.has(mapping.displayLabel)) {
            groups.get(mapping.displayLabel).count += count;
        } else {
            groups.set(mapping.displayLabel, {
                id: apiStatus.toLowerCase(),
                label: mapping.displayLabel,
                count,
            });
        }
    }

    return Array.from(groups.values());
};

export const getDisplayLabelForInternal = (internalDisplayValue, role) => {
    const display = getStatusDisplay(internalDisplayValue, role);
    return display.displayLabel;
};

export const getTrackerStatuses = (role) => {
    const roleGroup = getRoleGroup(role);
    const mappings = statusMapping.mappings[roleGroup];
    if (!mappings) return [];

    const seen = new Set();
    const result = [];

    const entries = Object.entries(mappings).sort(([, a], [, b]) => a.statusOrder - b.statusOrder);

    for (const [apiStatus, mapping] of entries) {
        if (seen.has(mapping.displayLabel)) continue;
        seen.add(mapping.displayLabel);

        const entry = Object.values(DEFICIENCY_STATUSES).find((s) => s.apiValue === apiStatus);

        result.push({
            displayLabel: mapping.displayLabel,
            apiValue: apiStatus,
            value: entry?.value || apiStatus,
            color: COLOR_MAP[mapping.colorToken],
            statusOrder: mapping.statusOrder,
        });
    }

    return result;
};
